import React, { useState, useRef, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import PasswordResetModal from "../../components/modals/password-reset-modal";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { saveSettingsAction, uploadLogoAction, viewSettingsAction } from "../../redux/actions/settingsAction";
import { errorToast, successToast } from "../../components/notifications/toast";
import Loader from "../../components/loaders/dot-loader";
import { setFavicon } from "../../components/helper";
import Button from "../../components/buttons/button";
import Switch from "../../components/buttons/switch";
import MapModal from "../../components/modals/map-modal";

const Settings = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const imageInputRef = useRef(null);
    const [settingsData, setsettingsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isGstEnabled, setisGstEnabled] = useState(false);
    let handleResetFunc = null;
    const [dirty, setDirty] = useState(false);
    const [isModalOpen, setModalOpen] = useState(false);
    const [location, setLocation] = useState({ lat: null, long: null });

    const initialValues = {
        name: settingsData?.company?.name || "",
        owner_name: settingsData?.company?.owner_name || "",
        email: settingsData?.company?.email || "",
        mobile: settingsData?.company?.mobile || "",
        logo: settingsData?.company?.logo || "",
        low_stock_alert: settingsData?.company?.low_stock_alert || "",
        printPreference: settingsData?.company?.printPreference || "A4",
        gstEnabled: settingsData?.company?.gstEnabled || false, // Ensure it's boolean
        gstNumber: settingsData?.company?.gstNumber || "",
        gstOptions: settingsData?.company?.gstOptions || [],
        gstPercentage: settingsData?.company?.gstPercentage || "",
        address: settingsData?.company?.address || "",
        latitude: settingsData?.company?.latitude || "",
        longitude: settingsData?.company?.longitude || "",
    };

    const validationSchema = Yup.object({
        name: Yup.string().required("Required!"),
        owner_name: Yup.string().required("owner_name is required"),
        email: Yup.string()
            .email("Invalid email address")
            .required("Email is required"),
        mobile: Yup.string()
            .matches(/^\d{10}$/, "Mobile number must be 10 digits")
            .required("Mobile number is required"),
        gstNumber: isGstEnabled ? Yup.string().required("Required!") : Yup.string().nullable(),
        gstPercentage: isGstEnabled ? Yup.number().min(0, "Percentage cannot be negative")
            .max(100, "Percentage cannot exceed 100").required("Required!") : Yup.string().nullable(),
        gstOptions: isGstEnabled ? Yup.array().min(1, "Atleast one should be selected") : ""

    });

    const handleSubmit = (values) => {
        console.log("Updated Settings:", values);
        values.latitude = location?.lat;
        values.longitude = location?.long;
        setLoading(true)
        dispatch(saveSettingsAction(values, (data) => { setLoading(false); successToast(data.data.message); fetchDetails() }, onError))
    };

    const handleLogoChange = (event, setFieldValue) => {
        const imageUploadSuccess = (data) => {
            console.log(data.data, "success data");
            setImagePreview(data.data.data);
            setFieldValue('logo', data.data.data)
        }
        const file = event.target.files[0];
        dispatch(uploadLogoAction(file, imageUploadSuccess, onError));

    };

    const handleRemoveImage = (setFieldValue) => {
        setImagePreview(null);
        setFieldValue("logo", null);
        if (imageInputRef.current) {
            imageInputRef.current.value = null;
        }
    };

    useEffect(() => {
        fetchDetails()
    }, []);

    const fetchDetails = async () => {
        setLoading(true)
        dispatch(viewSettingsAction({}, onSuccess, onError))
    };


    const onSuccess = (data) => {
        setsettingsData(data.data.data);
        setImagePreview(data.data.data.company.logo);
        setisGstEnabled(data.data.data.company?.gstEnabled);
        setLocation({ lat: data.data.data?.company?.latitude, long: data.data.data?.company?.longitude })
        if (data.data.data.company.logo) {
            setFavicon(data.data.data?.company?.logo)
        }
        setLoading(false)
    }

    const onError = (data) => {
        const errorMsg = data.data.message || "Unable to fetch try later";
        errorToast(errorMsg)
        setLoading(false)
    }

    if (loading) {
        return <div className="flex items-center justify-center h-screen">
            <Loader />
        </div>;
    }


    return (
        <div className="flex items-center justify-center bg-gray-100" style={{ height: "95vh" }}>
            <div className="w-full mx-6 bg-white shadow-lg rounded-md flex flex-col" style={{ height: '95%' }}>
                {/* Header Section */}
                <div className="flex justify-between items-center p-4 border-b">
                    <button onClick={() => navigate(-1)} className="text-blue-500 hover:text-blue-700 text-nowrap">
                        &lt; Back
                    </button>
                    <div className="flex gap-4">
                        <button
                            onClick={() => setModalOpen(true)}
                            className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 focus:outline-none"
                        >
                            Update Location
                        </button>
                        <button
                            type="button"
                            onClick={() => setIsPasswordModalOpen(true)}
                            className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600 focus:outline-none"
                        >
                            Reset Password
                        </button>
                    </div>

                </div>

                {/* Form Section */}
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {({ values, setFieldValue, resetForm, dirty }) => {
                        handleResetFunc = resetForm;
                        setDirty(dirty);
                        return (
                            <Form id="settingsForm" className="flex-grow overflow-auto ">
                                <div className="flex p-5 flex-wrap md:gap-8 lg:gap-40">
                                    <div className="grid gap-6">
                                        {/* Organization Name */}
                                        <div className="w-full max-w-96 sm:max-w-96 md:w-96">
                                            <div className="flex justify-between">
                                                <label htmlFor="name" className="">
                                                    Organization Name
                                                </label>
                                                <ErrorMessage
                                                    name="name"
                                                    component="div"
                                                    className="text-red-500 text-sm mt-1"
                                                />
                                            </div>

                                            <Field
                                                type="text"
                                                name="name"
                                                id="name"
                                                className="input-field"
                                                placeholder="Enter organization name"
                                            />

                                        </div>

                                        {/* owner_name */}
                                        <div className="w-full max-w-96 sm:max-w-96 md:w-96">
                                            <div className="flex justify-between">
                                                <label htmlFor="owner_name" className="">
                                                    Owner name
                                                </label>
                                                <ErrorMessage
                                                    name="owner_name"
                                                    component="div"
                                                    className="text-red-500 text-sm mt-1"
                                                />
                                            </div>
                                            <Field
                                                type="text"
                                                name="owner_name"
                                                id="owner_name"
                                                className="input-field"
                                                placeholder="Enter your owner_name"
                                            />
                                        </div>

                                        {/* Email */}
                                        <div className="w-full max-w-96 sm:max-w-96 md:w-96">
                                            <div className="flex justify-between">
                                                <label htmlFor="email" className="">
                                                    Email
                                                </label>
                                                <ErrorMessage
                                                    name="email"
                                                    component="div"
                                                    className="text-red-500 text-sm mt-1"
                                                />
                                            </div>
                                            <Field
                                                type="email"
                                                name="email"
                                                id="email"
                                                className="input-field"
                                                placeholder="Enter your email"
                                            />

                                        </div>

                                        {/* Mobile */}
                                        <div className="w-full max-w-96 sm:max-w-96 md:w-96">
                                            <div className="flex justify-between">
                                                <label htmlFor="mobile" className="">
                                                    Mobile
                                                </label>
                                                <ErrorMessage
                                                    name="mobile"
                                                    component="div"
                                                    className="text-red-500 text-sm mt-1"
                                                />
                                            </div>
                                            <Field
                                                type="text"
                                                name="mobile"
                                                id="mobile"
                                                className="input-field"
                                                placeholder="Enter your mobile number"
                                            />
                                        </div>

                                        <div className="w-full max-w-96 sm:max-w-96 md:w-96">
                                            <div className="flex justify-between">
                                                <label htmlFor="low_stock_alert" className="">
                                                    Low Stock Alert
                                                </label>
                                                <ErrorMessage
                                                    name="low_stock_alert"
                                                    component="div"
                                                    className="text-red-500 text-sm mt-1"
                                                />
                                            </div>
                                            <Field
                                                type="text"
                                                name="low_stock_alert"
                                                id="low_stock_alert"
                                                className="input-field"
                                                placeholder="Enter your low stock alert quantity"
                                            />

                                        </div>

                                        {/* Logo */}

                                        <div className="w-full max-w-96 sm:max-w-96 md:w-96">
                                            <div className="flex justify-between">
                                                <label htmlFor="logo" className="block  mb-2">
                                                    Logo
                                                </label>
                                                <></>
                                            </div>
                                            <input
                                                type="file"
                                                id="logo"
                                                name="logo"
                                                accept="image/*"
                                                className="w-full px-4 py-2 border rounded-md input-field"
                                                onChange={(e) => handleLogoChange(e, setFieldValue)}
                                                ref={imageInputRef}
                                            />
                                            {imagePreview && (
                                                <div className="mt-4 flex gap-3 items-start">
                                                    <img
                                                        src={imagePreview}
                                                        alt="Preview"
                                                        className="w-32 h-28 object-cover rounded-md border"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveImage(setFieldValue)}
                                                        className="mt-2 text-red-500 underline text-sm"
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                    </div>
                                    <div className="">
                                        <div className="w-full max-w-96 sm:max-w-96 md:w-96 mb-8">
                                            <label className="block mb-2 text-start">Print Preference</label>
                                            <div className="flex gap-4 px-2">
                                                <label className="flex items-center gap-2">
                                                    <Field type="radio" name="printPreference" value="A4" />
                                                    A4
                                                </label>
                                                <label className="flex items-center gap-2">
                                                    <Field type="radio" name="printPreference" value="Landscape" />
                                                    Landscape
                                                </label>
                                            </div>
                                        </div>

                                        <div className="w-full max-w-96 sm:max-w-96 md:w-96 mb-10">
                                            <div className="flex justify-between">
                                                <label htmlFor="address" className="">
                                                    Address
                                                </label>
                                                <ErrorMessage
                                                    name="address"
                                                    component="div"
                                                    className="text-red-500 text-sm mt-1"
                                                />
                                            </div>
                                            <Field
                                                type="text"
                                                name="address"
                                                id="address"
                                                className="input-field"
                                                placeholder="Enter your low stock alert quantity"
                                            />

                                        </div>

                                        <div className="w-full max-w-96 sm:max-w-96 md:w-96">
                                            <div className="flex items-center gap-3">
                                                <div className="mb-4">
                                                    <Field
                                                        name="gstEnabled"
                                                        id="gstEnabled"
                                                        label="Enable GST"
                                                        component={Switch}
                                                    />
                                                </div>
                                            </div>

                                            {values.gstEnabled && (
                                                <div className="mt-3 space-y-3 px-3">
                                                    <div>
                                                        <div className="flex items-center justify-between">
                                                            <label className="block text-start mb-1">GST Number</label>
                                                            <ErrorMessage
                                                                name="gstNumber"
                                                                component="div"
                                                                className="text-red-500 text-sm"
                                                            />
                                                        </div>
                                                        <Field
                                                            type="text"
                                                            name="gstNumber"
                                                            className="input-field"
                                                            placeholder="Enter GST Number"
                                                        />

                                                    </div>

                                                    <div>
                                                        <div className="flex justify-between items-center">
                                                            <label className="block text-start mb-1">GST Options</label>
                                                            <ErrorMessage
                                                                name="gstOptions"
                                                                component="div"
                                                                className="text-red-500 text-sm"
                                                            />
                                                        </div>
                                                        <div className="flex gap-4">
                                                            <label className="flex items-center gap-2">
                                                                <Field
                                                                    type="checkbox"
                                                                    name="gstOptions"
                                                                    value="SGST"
                                                                    checked={values.gstOptions.includes("SGST")}
                                                                    onChange={(e) => {
                                                                        const checked = e.target.checked;
                                                                        setFieldValue(
                                                                            "gstOptions",
                                                                            checked
                                                                                ? [...values.gstOptions, "SGST"]
                                                                                : values.gstOptions.filter((opt) => opt !== "SGST")
                                                                        );
                                                                    }}
                                                                />
                                                                SGST
                                                            </label>
                                                            <label className="flex items-center gap-2">
                                                                <Field
                                                                    type="checkbox"
                                                                    name="gstOptions"
                                                                    value="CGST"
                                                                    checked={values.gstOptions.includes("CGST")}
                                                                    onChange={(e) => {
                                                                        const checked = e.target.checked;
                                                                        setFieldValue(
                                                                            "gstOptions",
                                                                            checked
                                                                                ? [...values.gstOptions, "CGST"]
                                                                                : values.gstOptions.filter((opt) => opt !== "CGST")
                                                                        );
                                                                    }}
                                                                />
                                                                CGST
                                                            </label>
                                                            <label className="flex items-center gap-2">
                                                                <Field
                                                                    type="checkbox"
                                                                    name="gstOptions"
                                                                    value="IGST"
                                                                    checked={values.gstOptions.includes("IGST")}
                                                                    onChange={(e) => {
                                                                        const checked = e.target.checked;
                                                                        setFieldValue(
                                                                            "gstOptions",
                                                                            checked
                                                                                ? [...values.gstOptions, "IGST"]
                                                                                : values.gstOptions.filter((opt) => opt !== "IGST")
                                                                        );
                                                                    }}
                                                                />
                                                                IGST
                                                            </label>
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <div className="flex items-center justify-between">
                                                            <label className="block text-start mb-1">GST Percentage</label>
                                                            <ErrorMessage
                                                                name="gstPercentage"
                                                                component="div"
                                                                className="text-red-500 text-sm"
                                                            />
                                                        </div>
                                                        <Field
                                                            type="text"
                                                            name="gstPercentage"
                                                            className="input-field"
                                                            placeholder="Enter GST Percentage"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </Form>
                        );
                    }}
                </Formik>

                {/* Buttons Section */}
                <div className="flex justify-end gap-4 p-5 border-t">
                    <button
                        type="reset"
                        onClick={() => handleResetFunc && handleResetFunc()}
                        className="bg-gray-400 text-white px-6 py-2 rounded-md hover:bg-gray-500 focus:outline-none"
                    >
                        Reset
                    </button>
                    <button
                        // disabled={!dirty}
                        type="submit"
                        form="settingsForm"
                        className="bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600 focus:outline-none"
                    >
                        Save
                    </button>
                </div>

                {isPasswordModalOpen && (
                    <PasswordResetModal
                        isOpen={isPasswordModalOpen}
                        onClose={() => setIsPasswordModalOpen(false)}
                        resetingEmail={settingsData?.admin?.email}
                    />
                )}
                <MapModal
                    isOpen={isModalOpen}
                    onClose={() => setModalOpen(false)}
                    location={location}
                    setLocation={setLocation}
                />
            </div>
        </div>
    );
};

export default Settings;
